// Utility function to convert image URLs (NO PROXY - direct static serving like projects)
export const getImageProxyUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';
  
  // For external URLs (https/http from other domains), return as is
  if (imageUrl.startsWith('https://') || (imageUrl.startsWith('http://') && !imageUrl.includes('localhost'))) {
    return imageUrl;
  }
  
  // For API URLs, convert to relative paths for direct static serving
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl && imageUrl.startsWith(apiUrl)) {
    return imageUrl.replace(apiUrl, '');
  }
  
  // If it's already a relative path starting with /uploads/, keep as is
  if (imageUrl.startsWith('/uploads/')) {
    return imageUrl;
  }
  
  // Default: return as is
  return imageUrl;
};

/**
 * Normalize any image URL to the correct serving path.
 * Handles:
 *  - External URLs (http/https) → returned as-is
 *  - /api/images/... paths → returned as-is (served by backend static + proxy)
 *  - /uploads/images/... paths → converted to /api/images/... for CORS
 *  - Bare filenames → resolved to /api/images/projects/logos/<filename>
 *  - Previously failed URLs → returns PLACEHOLDER_IMAGE immediately (no network request)
 */

// Module-level cache of URLs that already returned 404.
// Prevents repeated network requests on React re-renders (auth checks, window focus, etc.)
const failedImageUrls = new Set<string>();

export const getFullImageUrl = (imageUrl: string | undefined | null): string => {
  if (!imageUrl) return '';

  const url = imageUrl.trim();

  // Check failed cache BEFORE any transformation (using raw DB value)
  if (failedImageUrls.has(url)) {
    return PLACEHOLDER_IMAGE;
  }

  let resolved: string;

  // External URLs — pass through
  if (url.startsWith('http://') || url.startsWith('https://')) {
    resolved = url;
  }
  // Already using /api/images — good
  else if (url.startsWith('/api/images/')) {
    resolved = url;
  }
  // /uploads/images/... → rewrite to /api/images/...
  else if (url.startsWith('/uploads/images/')) {
    resolved = url.replace('/uploads/images/', '/api/images/');
  }
  // /uploads/... (legacy) → try to map to /api/images/
  else if (url.startsWith('/uploads/')) {
    resolved = url.replace('/uploads/', '/api/images/');
  }
  // Bare filename (e.g. "logo-123.png") → assume project logo
  else if (!url.startsWith('/')) {
    resolved = `/api/images/projects/logos/${url}`;
  }
  // Any other relative path
  else {
    resolved = url;
  }

  // Also check resolved URL against failed cache
  if (failedImageUrls.has(resolved)) {
    return PLACEHOLDER_IMAGE;
  }

  return resolved;
};

/** Default placeholder SVG as data URI (grey box with landscape icon) */
export const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' fill='%231f2937'/%3E%3Cpath d='M28 52l10-20 7 14 5-7 8 13H22z' fill='%234b5563'/%3E%3Ccircle cx='54' cy='30' r='6' fill='%234b5563'/%3E%3C/svg%3E";

/**
 * onError handler for <img> tags — caches the failed URL and swaps to placeholder.
 * On the NEXT React re-render, getFullImageUrl() will return PLACEHOLDER_IMAGE
 * directly — no network request, no console error.
 *
 * Usage:  <img src={getFullImageUrl(url)} onError={handleImageError} />
 */
export const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  const img = e.currentTarget;

  // Already showing placeholder — nothing to do
  if (img.src.startsWith('data:')) return;

  // Cache the failed URL (as pathname, matching getFullImageUrl output)
  try {
    const parsed = new URL(img.src);
    failedImageUrls.add(parsed.pathname);  // e.g. "/api/images/projects/logos/logo-xxx.png"
    failedImageUrls.add(img.src);          // full URL for external images
  } catch {
    failedImageUrls.add(img.src);
  }

  // Immediately show placeholder in the DOM (until React re-renders with cached result)
  img.src = PLACEHOLDER_IMAGE;
};

export default getImageProxyUrl;